/**
 * Tap tempo y calibración de la latencia (§5.2, §5.3). Las dos cosas son lo mismo visto desde
 * dos lados: toques del usuario medidos contra un reloj. Todo es cálculo puro sobre instantes en
 * segundos; quién toca y con qué reloj lo decide la interfaz.
 */

/** Un hueco mayor que este entre dos toques empieza una medida nueva. */
const TAP_RESET = 2
const TAP_WINDOW = 8
export const MIN_BPM = 20
export const MAX_BPM = 320

const median = (values: readonly number[]) => {
  const sorted = [...values].sort((a, b) => a - b)
  const middle = Math.floor(sorted.length / 2)
  return sorted.length % 2 === 1 ? sorted[middle]! : (sorted[middle - 1]! + sorted[middle]!) / 2
}

/** Los toques que cuentan: los últimos seguidos, sin un hueco largo entre ellos. */
export function recentTaps(taps: readonly number[]): number[] {
  let start = 0
  for (let index = 1; index < taps.length; index += 1) {
    if (taps[index]! - taps[index - 1]! > TAP_RESET) start = index
  }
  return taps.slice(Math.max(start, taps.length - TAP_WINDOW))
}

/** El tempo de una serie de toques (la mediana de los intervalos), o null si faltan toques. */
export function tapTempo(taps: readonly number[]): number | null {
  const recent = recentTaps(taps)
  if (recent.length < 3) return null
  const intervals = recent.slice(1).map((time, index) => time - recent[index]!)
  const bpm = Math.round(60 / median(intervals))
  return Math.min(MAX_BPM, Math.max(MIN_BPM, bpm))
}

export interface LatencyMeasure {
  /** Retraso medio entre el clic programado y el toque, en segundos; null si no es fiable. */
  offset: number | null
  /** Dispersión de los toques (desviación absoluta mediana), en segundos. */
  spread: number
  /** Toques que se han usado. */
  used: number
}

/** Mínimo de toques útiles y dispersión máxima para dar la medida por buena. */
export const CALIBRATION_MIN_TAPS = 8
export const CALIBRATION_MAX_SPREAD = 0.04

/**
 * Calibración: el usuario toca a la vez que oye los clics. Cada toque se empareja con el clic más
 * cercano y la mediana de las diferencias es el retraso entre programar un sonido y percibirlo
 * (altavoz, Bluetooth y reacción). Los dos primeros toques se descartan mientras se coge el ritmo.
 */
export function measureLatency(
  clicks: readonly number[],
  taps: readonly number[],
  period: number,
): LatencyMeasure {
  const differences: number[] = []
  for (const tap of taps) {
    let best: number | null = null
    for (const click of clicks) {
      const difference = tap - click
      if (
        Math.abs(difference) < period / 2 &&
        (best === null || Math.abs(difference) < Math.abs(best))
      ) {
        best = difference
      }
    }
    if (best !== null) differences.push(best)
  }
  const useful = differences.slice(2)
  if (useful.length === 0) return { offset: null, spread: 0, used: 0 }
  const center = median(useful)
  const spread = median(useful.map((value) => Math.abs(value - center)))
  const reliable = useful.length >= CALIBRATION_MIN_TAPS && spread <= CALIBRATION_MAX_SPREAD
  return { offset: reliable ? center : null, spread, used: useful.length }
}
